import {
  PartialSalesforceFieldMappingLocalData,
  SalesforceFieldMappingLocalData,
  SalesforceMappingOption,
  SalesforceResourceMappingLocalData,
} from 'module/api/integrations/salesforce/types';
import { useSalesForceExternalResourceFields } from 'queries/salesforce';
import { useMemo } from 'react';
import { tv } from 'tailwind-variants';
import { sortByText } from 'utils/sort-by-text';
import { FieldMapping } from '../field-mapping';
import { EmptyState } from './empty-state';
import { Header } from './header';
import { Loader } from './loader';

const containerVariants = tv({
  base: 'mb-8 bg-white flex flex-col',
});

type FieldsObjectProps = {
  integrationId: number;
  fieldObject: SalesforceResourceMappingLocalData;
  onObjectRemove: (resourceId: number) => void;
  onMappingAdd: (
    resourceId: number,
    externalField: SalesforceMappingOption<boolean> | null,
  ) => void;
  onMappingRemove: (
    resourceId: number,
    fieldMapping: SalesforceFieldMappingLocalData,
  ) => void;
  onMappingChange: (
    resourceId: number,
    mappingId: string,
    payload: PartialSalesforceFieldMappingLocalData,
  ) => void;
};

export const FieldsObject = ({
  integrationId,
  fieldObject,
  onObjectRemove,
  onMappingAdd,
  onMappingRemove,
  onMappingChange,
}: FieldsObjectProps): React.JSX.Element => {
  const { data: externalFields = [], status: externalFieldsStatus } =
    useSalesForceExternalResourceFields(
      integrationId,
      fieldObject.externalResourceType,
    );

  const selectedExternalFieldsById = useMemo(
    () =>
      (fieldObject.fieldMappings ?? [])
        .map((fieldMapping) => fieldMapping.salesforceField?.value)
        .filter(Boolean),
    [fieldObject],
  );

  const externalFieldAsOptions = useMemo(
    () =>
      (externalFields ?? [])
        .filter((field) =>
          field?.allowMultipleMapping
            ? field?.allowMultipleMapping
            : !selectedExternalFieldsById.includes(field.integrationFieldId),
        )
        .map((field) => ({
          value: field.integrationFieldId,
          label: field.fieldName,
          extra: field.editableByFieldMapping,
          parent: field.parentFieldName ?? fieldObject.externalResourceType,
        }))
        .sort((a, b) => sortByText(a.label, b.label)),
    [externalFields, selectedExternalFieldsById, fieldObject],
  );

  const onExternalFieldSelect = (
    selected: SalesforceMappingOption<boolean> | null,
  ) => {
    if (selected) {
      onMappingAdd(fieldObject.resourceMappingConfigId, {
        label:
          selected.parent === fieldObject.externalResourceType
            ? selected.label
            : `[${selected.parent}] ${selected.label}`,
        value: selected.value.toString(),
        extra: selected.extra,
      });
    }
  };

  return (
    <div className={containerVariants()}>
      <Header
        name={fieldObject.externalResourceType}
        nativeResourceType={fieldObject.nativeResourceType}
        onRemove={() => onObjectRemove(fieldObject.resourceMappingConfigId)}
      />

      <div className="w-full">
        {externalFieldsStatus === 'pending' ? <Loader /> : null}
        {externalFieldsStatus === 'success' &&
        fieldObject.fieldMappings?.length ? (
          <div className="flex flex-col">
            {fieldObject.fieldMappings.map((fieldMapping) => {
              return fieldMapping && fieldMapping.salesforceField ? (
                <FieldMapping
                  integrationId={integrationId}
                  externalFields={externalFieldAsOptions}
                  fieldMap={fieldMapping}
                  onRemove={() =>
                    onMappingRemove(
                      fieldObject.resourceMappingConfigId,
                      fieldMapping,
                    )
                  }
                  salesforceFieldValue={fieldMapping.salesforceField.value.toString()}
                  onChange={onMappingChange}
                  key={fieldMapping.fieldMappingConfigId ?? fieldMapping.id}
                  fieldObject={fieldObject}
                />
              ) : null;
            })}
          </div>
        ) : null}
      </div>

      <EmptyState
        fields={externalFieldAsOptions}
        onChange={onExternalFieldSelect}
        fieldObject={fieldObject}
      />
    </div>
  );
};
